import axios from "axios";
import { baseURL } from "../apirest";

/**
 * Helper to dispatch Moodle Web Service REST calls via Axios.
 * Intercepts Moodle HTTP 200 exception responses (data.exception, data.errorcode, data.error)
 * and throws structured JavaScript errors.
 *
 * @param {Object} opts
 * @param {string} opts.token
 * @param {string} opts.wsfunction
 * @param {Object} [opts.params]
 * @returns {Promise<any>}
 */
export const callMoodleWS = async ({ token, wsfunction, baseUrl = baseURL, params = {} }) => {
  const wsBase = `${baseUrl.replace(/\/+$/, "")}/webservice/rest/server.php`;
  const search = new URLSearchParams({
    wstoken: token,
    moodlewsrestformat: "json",
    wsfunction,
  });

  Object.entries(params).forEach(([key, val]) => {
    if (val !== undefined && val !== null) {
      search.append(key, val);
    }
  });

  const { data } = await axios.get(`${wsBase}?${search.toString()}`);

  if (data && (data.exception || data.errorcode || data.error)) {
    const errorMsg = data.message || data.error || data.errorcode || "Moodle web service error";
    const error = new Error(errorMsg);
    error.exception = data.exception;
    error.errorcode = data.errorcode;
    throw error;
  }

  return data;
};

/**
 * 1. local_tipddy_duel_create_challenge
 */
export const createChallenge = async ({
  token,
  challengedid,
  courseid = 0,
  categoryid = 0,
  numquestions = 0,
}) =>
  callMoodleWS({
    token,
    wsfunction: "local_tipddy_duel_create_challenge",
    params: { challengedid, courseid, categoryid, numquestions },
  });

/**
 * 2. local_tipddy_duel_respond_challenge
 */
export const respondChallenge = async ({ token, challengeid, action }) =>
  callMoodleWS({
    token,
    wsfunction: "local_tipddy_duel_respond_challenge",
    params: { challengeid, action },
  });

/**
 * 3. local_tipddy_duel_list_my_duels
 */
export const listMyDuels = async ({ token, status = "", courseid = 0 }) =>
  callMoodleWS({
    token,
    wsfunction: "local_tipddy_duel_list_my_duels",
    params: { status, courseid },
  });

/**
 * 4. local_tipddy_duel_get_challenge
 */
export const getChallenge = async ({ token, challengeid }) =>
  callMoodleWS({
    token,
    wsfunction: "local_tipddy_duel_get_challenge",
    params: { challengeid },
  });

/**
 * 5. local_tipddy_duel_get_questions
 */
export const getQuestions = async ({ token, challengeid }) =>
  callMoodleWS({
    token,
    wsfunction: "local_tipddy_duel_get_questions",
    params: { challengeid },
  });

/**
 * 6. local_tipddy_duel_submit_answer
 */
export const submitAnswer = async ({ token, challengeid, questionid, answer, timetaken }) =>
  callMoodleWS({
    token,
    wsfunction: "local_tipddy_duel_submit_answer",
    params: { challengeid, questionid, answer, timetaken },
  });

/**
 * 7. local_tipddy_duel_finish_attempt
 */
export const finishAttempt = async ({ token, challengeid }) =>
  callMoodleWS({
    token,
    wsfunction: "local_tipddy_duel_finish_attempt",
    params: { challengeid },
  });

/**
 * 8. local_tipddy_duel_list_opponents
 *
 * Offset-based pagination. `query` is intentionally NOT sent: it is dead
 * code end-to-end on the backend (validated in external/list_opponents.php
 * and discarded there — it never reaches the service or repository), so a
 * name-search param would filter nothing server-side.
 *
 * Expected response shape: { opponents: [{id, fullname, ...}], total, offset, limit }.
 *
 * @param {Object} opts
 * @param {string} opts.token
 * @param {number} opts.courseid
 * @param {number} [opts.limit=20]
 * @param {number} [opts.offset=0]
 */
export const listOpponents = async ({ token, courseid, limit = 20, offset = 0 }) =>
  callMoodleWS({
    token,
    wsfunction: "local_tipddy_duel_list_opponents",
    params: { courseid, limit, offset },
  });

/**
 * 9. local_tipddy_duel_get_user_stats
 */
export const getUserStats = async ({ token, userid = 0, courseid = 0 }) =>
  callMoodleWS({
    token,
    wsfunction: "local_tipddy_duel_get_user_stats",
    params: { userid, courseid },
  });

/**
 * 10. local_tipddy_duel_expire_challenges
 */
export const expireChallenges = async ({ token }) =>
  callMoodleWS({
    token,
    wsfunction: "local_tipddy_duel_expire_challenges",
  });

/**
 * 11. local_tipddy_duel_get_challenge_review
 */
export const getChallengeReview = async ({ token, challengeid }) =>
  callMoodleWS({
    token,
    wsfunction: "local_tipddy_duel_get_challenge_review",
    params: { challengeid },
  });

/**
 * 12. local_tipddy_duel_list_categories
 */
export const listCategories = async ({ token, courseid = 0 }) =>
  callMoodleWS({
    token,
    wsfunction: "local_tipddy_duel_list_categories",
    params: { courseid },
  });

/**
 * Normalizes the two raw challenge shapes returned by the Moodle duel WS
 * layer into one canonical shape consumed by `BattleModal` and the
 * `/duels` page (design.md D5, spec `duel-data-layer` — "Canonical
 * Challenge Shape").
 *
 * Source shapes:
 * - `local_tipddy_duel_list_my_duels`: `{ id, opponent: {id, fullname,
 *   avatarurl}, role: "challenger"|"opponent", status, ... }`.
 * - `local_tipddy_duel_create_challenge` / `local_tipddy_duel_get_challenge`:
 *   `{ id, challengerid, challengedid, courseid, coursename, status, ... }`
 *   — no `opponent`/`role` fields.
 *
 * This is a pure function: it does not fetch, does not mutate `raw`, and is
 * meant to be applied via react-query `select`, never inside the service
 * functions above (those stay auth/view-agnostic).
 *
 * @param {Object} raw - Raw WS response for a single challenge.
 * @param {Object} opts
 * @param {number|string} opts.currentUserId - Needed only to derive `role`
 *   for the challenger/challenged shape, which has no `role` field of its
 *   own.
 * @returns {{
 *   id: (number|string),
 *   courseid: (number|undefined),
 *   coursename: (string|undefined),
 *   status: (string|undefined),
 *   role: ("challenger"|"opponent"|undefined),
 *   rival: ({id: (number|string)}|null),
 *   isCurrentUserTheOpponent: boolean,
 *   raw: Object,
 * }}
 */
export const normalizeChallenge = (raw, { currentUserId } = {}) => {
  let role = raw.role;
  const challengerId = raw.challengerid ?? raw.challenger?.id;
  const challengedId = raw.challengedid ?? raw.challenged?.id;

  if (role === undefined) {
    if (challengerId !== undefined && currentUserId !== undefined) {
      role = String(challengerId) === String(currentUserId) ? "challenger" : "opponent";
    } else {
      role = undefined;
    }
  }

  let rival = null;
  if (raw.opponent) {
    rival = raw.opponent;
  } else if (role === "challenger") {
    rival = raw.challenged ? raw.challenged : (challengedId !== undefined ? { id: challengedId } : null);
  } else if (role === "opponent") {
    rival = raw.challenger ? raw.challenger : (challengerId !== undefined ? { id: challengerId } : null);
  } else if (challengerId !== undefined || challengedId !== undefined) {
    rival = { id: role === "challenger" ? challengedId : challengerId };
  }

  const myAttempt =
    normalizeAttempt(raw.myattempt) ??
    (raw.myprogress ? { status: raw.myprogress } : null);

  const opponentAttempt =
    normalizeAttempt(raw.opponentattempt) ??
    (raw.opponentprogress ? { status: raw.opponentprogress } : null);

  return {
    id: raw.id ?? raw.challengeid,
    courseid: raw.courseid,
    coursename: raw.coursename,
    status: raw.status,
    role,
    rival,
    isCurrentUserTheOpponent: role === "opponent",
    myAttempt,
    opponentAttempt,
    result: raw.result,
    winnerid: raw.winnerid,
    myscore: raw.myscore,
    opponentscore: raw.opponentscore,
    timecreated: raw.timecreated,
    raw,
  };
};

/**
 * Pure shape guard for a single per-user attempt object embedded in a
 * `local_tipddy_duel_get_challenge` response (`myattempt`/`opponentattempt`).
 * Returns `null` on any shape mismatch (fail-open, design.md D2/D4) rather
 * than throwing, so a malformed/missing attempt never crashes a consumer —
 * it simply looks like "no attempt data available".
 *
 * @param {*} raw
 * @returns {{status: string, score: (number|undefined), timetaken:
 *   (number|undefined), timecompleted: (number|undefined)} | null}
 */
export const normalizeAttempt = (raw) => {
  if (!raw || typeof raw !== "object" || raw.status === undefined) {
    return null;
  }

  return {
    status: raw.status,
    score: raw.score,
    timetaken: raw.timetaken,
    timecompleted: raw.timecompleted,
  };
};

/**
 * Derives the `WAITING_OPPONENT` `resultData` shape from a normalized
 * challenge's `myAttempt` (design.md D2/D4, spec `duel-resume-guard` Req 4).
 * Only trusts the exact string `"finished"` — `local_tipddy_duel_get_challenge`
 * declares `status` as `PARAM_ALPHA`, which strips underscores server-side
 * (e.g. `not_started` may arrive as `notstarted`), so this MUST stay an
 * allow-one positive match, never a deny-list.
 *
 * @param {Object|null|undefined} challenge - Normalized challenge (with
 *   `myAttempt`), as produced by `normalizeChallenge`.
 * @returns {{myscore: number, mytimetaken: number} | null}
 */
export const deriveWaitingResultData = (challenge) => {
  const myAttempt = challenge?.myAttempt;
  if (!myAttempt || myAttempt.status !== "finished") {
    return null;
  }

  return {
    myscore: myAttempt.score,
    mytimetaken: myAttempt.timetaken ?? myAttempt.timecompleted,
  };
};

/**
 * Derives the `RESULT` `resultData` shape from a finished challenge.
 *
 * @param {Object|null|undefined} challenge - Normalized challenge, as produced by `normalizeChallenge`.
 * @returns {Object|null}
 */
export const deriveCompletedResultData = (challenge) => {
  if (!challenge || challenge.status !== "finished") {
    return null;
  }
  const myAttempt = challenge.myAttempt;
  const oppAttempt = challenge.opponentAttempt;
  const myscore = myAttempt?.score ?? 0;
  const opponentscore = oppAttempt?.score ?? 0;
  const isdraw = myscore === opponentscore;
  const iswinner = myscore > opponentscore;
  return {
    myscore,
    mytimetaken: myAttempt?.timetaken ?? myAttempt?.timecompleted ?? 0,
    opponentscore,
    opponenttimetaken: oppAttempt?.timetaken ?? oppAttempt?.timecompleted ?? 0,
    iswinner,
    isdraw,
    status: "finished",
  };
};
